import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
import {
  Paper,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

// GraphQL запит для отримання статистики по секціях за рік
const GET_SECTIONS_STATS = gql`
  query GetSectionsStats($inputFilter: StatsSectionsFilterInputType) {
    statsSections(inputFilter: $inputFilter) {
      sectionName
      sectionId
      countMembers
      sumContributions
      countPaidMembers
      percent
    }
  }
`;

// Генерація динамічного заголовка
const generateTitle = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 4;
  return `Кількість спортсменів по видах спорту за ${startYear}-${currentYear}`;
};

// Кольори для секцій
const SECTION_COLORS = {
  'Карате': '#1976d2',
  'Кікбоксинг/Тайський бокс': '#388e3c',
  'Художня гімнастика/акробатика': '#f57c00',
  'Ігрові види спорту': '#7b1fa2',
  'Греплінг/ММА/Дзюдо': '#d32f2f',
  'Бокс': '#303f9f',
  'Айкідо': '#689f38',
  'Інші': '#795548',
};

const StaticChart = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Генеруємо роки динамічно (останні 5 років)
  const currentYear = new Date().getFullYear();
  const years = [currentYear-4, currentYear-3, currentYear-2, currentYear-1, currentYear];

  // Отримуємо дані за останні 5 років динамічно
  const { data: dataYear1, loading: loadingYear1 } = useQuery(GET_SECTIONS_STATS, {
    variables: { inputFilter: { year: years[0].toString() } },
    errorPolicy: 'all',
  });
  const { data: dataYear2, loading: loadingYear2 } = useQuery(GET_SECTIONS_STATS, {
    variables: { inputFilter: { year: years[1].toString() } },
    errorPolicy: 'all',
  });
  const { data: dataYear3, loading: loadingYear3 } = useQuery(GET_SECTIONS_STATS, {
    variables: { inputFilter: { year: years[2].toString() } },
    errorPolicy: 'all',
  });
  const { data: dataYear4, loading: loadingYear4 } = useQuery(GET_SECTIONS_STATS, {
    variables: { inputFilter: { year: years[3].toString() } },
    errorPolicy: 'all',
  });
  const { data: dataYear5, loading: loadingYear5 } = useQuery(GET_SECTIONS_STATS, {
    variables: { inputFilter: { year: years[4].toString() } },
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (dataYear1 && dataYear2 && dataYear3 && dataYear4 && dataYear5 && 
        !loadingYear1 && !loadingYear2 && !loadingYear3 && !loadingYear4 && !loadingYear5) {
      
      // Створюємо структуру даних для графіка
      const processYearData = (data, year) => {
        const yearData = { year };
        data.statsSections.forEach(section => {
          if (section.sectionName !== 'Всього') {
            yearData[section.sectionName] = section.countPaidMembers;
          }
        });
        return yearData;
      };

      const rawData = [
        { year: 0, 'Карате': 0, 'Кікбоксинг/Тайський бокс': 0, 'Художня гімнастика/акробатика': 0, 'Ігрові види спорту': 0, 'Греплінг/ММА/Дзюдо': 0, 'Бокс': 0, 'Айкідо': 0, 'Інші': 0 },
        processYearData(dataYear1, years[0]),
        processYearData(dataYear2, years[1]),
        processYearData(dataYear3, years[2]),
        processYearData(dataYear4, years[3]),
        processYearData(dataYear5, years[4]),
      ];

      // Підготовка даних для Chart.js
      const labels = rawData.map(item => item.year === 0 ? '0' : item.year.toString());
      const datasets = Object.keys(SECTION_COLORS).map((sectionName) => ({
        label: sectionName,
        data: rawData.map(item => item[sectionName] || 0),
        borderColor: SECTION_COLORS[sectionName],
        backgroundColor: SECTION_COLORS[sectionName] + '40',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }));

      setChartData({
        labels,
        datasets,
      });

      setLoading(false);
    }
  }, [dataYear1, dataYear2, dataYear3, dataYear4, dataYear5, loadingYear1, loadingYear2, loadingYear3, loadingYear4, loadingYear5, years]);

  const sections = Object.keys(SECTION_COLORS);
  const title = generateTitle();

  if (loading || loadingYear1 || loadingYear2 || loadingYear3 || loadingYear4 || loadingYear5) {
    return (
      <Paper style={{ padding: '20px', textAlign: 'center', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: '10px' }}>
          Завантаження даних...
        </Typography>
      </Paper>
    );
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // Прибираємо легенду над графіком
      },
      tooltip: {
        animation: {
          duration: 100, // Швидкість появи tooltip в мілісекундах (за замовчуванням 400)
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y;
            return label;
          },
          title: function(context) {
            const label = context[0].label;
            return label === '0' ? 'Нульова точка' : `Рік: ${label}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Paper style={{ 
      padding: '20px', 
      height: window.innerWidth <= 768 ? 'auto' : '1000px',
      overflow: 'hidden',
      maxHeight: window.innerWidth <= 768 ? '95vh' : '100vh'
    }}>
      <Typography variant="h6" gutterBottom>
        Динаміка зростання по секціях
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '20px' }}>
        {title}
      </Typography>

      {chartData && (
        <div style={{ height: window.innerWidth <= 768 ? 300 : 800 }}>
          <Line data={chartData} options={options} />
        </div>
      )}

      {/* Легенда з поясненням */}
      <div style={{ marginTop: '20px' }}>
        <Typography variant="subtitle2" gutterBottom>
          Пояснення ліній:
        </Typography>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '4px',
          marginTop: '10px',
          justifyContent: 'flex-start'
        }}>
          {sections.map((sectionName) => (
            <div key={sectionName} style={{ 
              display: 'flex', 
              alignItems: 'center',
              marginRight: '8px',
              marginBottom: '4px',
              whiteSpace: 'nowrap'
            }}>
              <div style={{ 
                width: '10px', 
                height: '10px', 
                backgroundColor: SECTION_COLORS[sectionName],
                marginRight: '4px',
                flexShrink: 0
              }} />
              <Typography variant="body2" style={{ 
                color: SECTION_COLORS[sectionName],
                fontSize: '11px',
                lineHeight: 1.2,
                whiteSpace: 'nowrap'
              }}>
                {sectionName}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </Paper>
  );
};

export default StaticChart;
